import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { V2AdminListInvestorUpdateProposalsResponses } from '@workspace/api-backend'
import {
  v2AdminDecideInvestorUpdateProposalMutation,
  v2AdminGetOutreachMessageConversationOptions,
  v2AdminListInvestorUpdateProposalsOptions,
} from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Input } from '@workspace/ui/components/input'
import { Pagination } from '@workspace/ui/components/pagination'
import { useState } from 'react'

type SearchParams = {
  page?: number
  status?: string
  action?: string
}

export const Route = createFileRoute('/admin/investors/update-proposals')({
  component: AdminInvestorUpdateProposalsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    status: (search.status as string) || undefined,
    action: (search.action as string) || undefined,
  }),
})

type Item = V2AdminListInvestorUpdateProposalsResponses[200]['data']['items'][number]
type Action = Item['action']
type Status = Item['status']

const LIMIT = 50
const STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const
const ACTIONS = ['RETYPE_AGENT_ADVISOR', 'MARK_INACTIVE', 'EXCLUDE_CONTACT'] as const

// What each action actually does, in the words of the consequence rather than
// the enum — an admin approving one is removing somebody from every founder's
// match pool and should be able to read that off the row.
// Named after the WRITE each one makes, not after the reply that prompted it.
// The first version named them after the reply ("Not an investor", "Bad
// contact"), which put them in the same vocabulary as the classifier labels
// rendered beside them — so a row read "Bad contact · not an investor" and gave
// no clue which pill was the proposed action and which was the evidence.
const ACTION_COPY: Record<Action, { label: string; effect: string }> = {
  RETYPE_AGENT_ADVISOR: {
    label: 'Retype fund → Agent / Advisor',
    effect: 'Types the whole fund as Agent / Advisor — drops it from all matching, permanently.',
  },
  MARK_INACTIVE: {
    label: 'Pause fund',
    effect: 'Pauses the fund until the date below. It re-enters matching by itself.',
  },
  EXCLUDE_CONTACT: {
    label: 'Exclude this person',
    effect:
      'Stops outreach to this person under every fund they are listed on. The fund stays. Set a date to pause them until then instead of excluding for good.',
  },
}

const ACTION_VARIANT: Record<Action, 'destructive' | 'warning' | 'secondary'> = {
  RETYPE_AGENT_ADVISOR: 'destructive',
  MARK_INACTIVE: 'warning',
  EXCLUDE_CONTACT: 'secondary',
}

const STATUS_VARIANT: Record<Status, 'success' | 'secondary' | 'destructive'> = {
  PENDING: 'secondary',
  APPROVED: 'success',
  REJECTED: 'destructive',
}

function formatDate(date: string | Date | null): string {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** `2027-10-01` for a date input, in local time — `toISOString` would shift it. */
function toDateInputValue(date: string | Date | null): string {
  if (!date) return ''
  const d = new Date(date)
  const month = `${d.getMonth() + 1}`.padStart(2, '0')
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

/**
 * The inverse of toDateInputValue, in the same timezone it wrote.
 *
 * `new Date('2027-10-01')` is defined as UTC midnight, while the value above is
 * built from local getters — so west of UTC, an admin who opened the picker and
 * re-selected the date already shown submitted the day before. Constructing
 * from parts keeps both halves local.
 */
function fromDateInputValue(value: string): Date | undefined {
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return undefined
  return new Date(year, month - 1, day)
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
}

/**
 * The LinkedIn conversation behind a proposal, fetched live on expand.
 *
 * Deliberately not read from our database: the transcript is held only for the
 * lifetime of a classification run and nulled afterwards, so there is nothing
 * stored to show. This reads through to HeyReach each time and stores nothing.
 */
function ConversationPanel({ messageId }: { messageId: string }) {
  const { data, isLoading, isError } = useQuery(
    v2AdminGetOutreachMessageConversationOptions({ path: { messageId } })
  )

  if (isLoading) {
    return <PanelShell>Fetching the conversation from LinkedIn…</PanelShell>
  }
  if (isError) {
    return <PanelShell>Could not reach HeyReach. Try expanding again.</PanelShell>
  }

  const conversation = data?.data
  if (!conversation?.available) {
    return (
      <PanelShell>{conversation?.unavailableReason ?? 'No conversation available.'}</PanelShell>
    )
  }
  if (conversation.messages.length === 0) {
    return <PanelShell>HeyReach has no messages on record for this contact.</PanelShell>
  }

  return (
    <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-4">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
        Live from LinkedIn · not stored
      </p>
      {conversation.messages.map((m, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: a thread has no message ids; order is the identity
          key={i}
          className={`flex flex-col gap-0.5 ${m.role === 'sender' ? 'items-end text-right' : 'items-start'}`}
        >
          <span className="text-[11px] text-muted-foreground">
            {m.role === 'sender' ? 'Founder' : (m.sender ?? 'Investor')} · {formatTime(m.sentAt)}
          </span>
          <p
            className={`max-w-[70%] whitespace-pre-wrap rounded-lg px-3 py-2 text-xs ${
              m.role === 'sender' ? 'bg-primary/10' : 'bg-card'
            }`}
          >
            {m.text}
          </p>
        </div>
      ))}
    </div>
  )
}

function PanelShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-xs text-muted-foreground">
      {children}
    </div>
  )
}

function AdminInvestorUpdateProposalsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const currentPage = search.page ?? 1
  const selectedStatus = (search.status as Status | undefined) ?? 'PENDING'
  const selectedAction = search.action as Action | undefined

  // Per-row edits to the proposed pause date, keyed by proposal id. An admin
  // who knows the fund is often correcting a date the classifier inferred.
  const [dateEdits, setDateEdits] = useState<Record<string, string>>({})
  // Which proposal's conversation is open. One at a time — each expand is a
  // live HeyReach call, and stacking them would fan out requests for no gain.
  const [openConversationId, setOpenConversationId] = useState<string | null>(null)

  const queryOptions = v2AdminListInvestorUpdateProposalsOptions({
    query: { page: currentPage, status: selectedStatus, action: selectedAction },
  })
  const { data: response, isLoading } = useQuery(queryOptions)

  const decide = useMutation({
    ...v2AdminDecideInvestorUpdateProposalMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryOptions.queryKey }),
  })

  const items: Item[] = response?.data?.items ?? []
  const total: number = response?.data?.total ?? 0
  const totalPages = Math.ceil(total / LIMIT)

  const setSearch = (next: Partial<SearchParams>) =>
    void navigate({
      search: { page: 1, status: selectedStatus, action: selectedAction, ...next },
    })

  const columns: TableColumn<Item>[] = [
    {
      header: 'Target',
      width: '260px',
      render: (item) => (
        <div className="flex flex-col leading-tight">
          <a
            href={`/admin/investors/${item.investorId}`}
            className="text-sm text-primary hover:underline"
          >
            {item.investorName}
          </a>
          {item.contactName ? (
            <span className="text-xs text-foreground">
              {item.contactName}
              {item.contactTitle ? (
                <span className="text-muted-foreground"> · {item.contactTitle}</span>
              ) : null}
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground">
              {item.investorType ?? 'untyped'} · fund-level
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Evidence',
      render: (item) => (
        <div className="flex flex-col items-start gap-1">
          <span className="block max-w-lg whitespace-normal break-words text-xs text-muted-foreground">
            {item.evidence}
          </span>
          {item.sourceMessageId ? (
            <button
              type="button"
              onClick={() => setOpenConversationId(openConversationId === item.id ? null : item.id)}
              className="text-xs text-primary hover:underline"
            >
              {openConversationId === item.id ? 'Hide conversation' : 'Read the conversation'}
            </button>
          ) : (
            // Detector proposals are raised from reply counts, not a thread.
            <span className="text-[11px] text-muted-foreground/70">
              No single reply behind this
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Proposal',
      width: '300px',
      render: (item) => (
        <div className="flex flex-col gap-1">
          <Badge variant={ACTION_VARIANT[item.action]} size="sm">
            {ACTION_COPY[item.action].label}
          </Badge>
          <span className="flex flex-wrap items-center gap-1">
            {/* The classifier's read, kept visually subordinate to the action —
                these are why it was raised, not what approving does. */}
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
              {item.labels.length > 0 ? 'Reply' : 'Source'}
            </span>
            {item.labels.length > 0 ? (
              item.labels.map((label) => (
                <span key={label} className="text-[11px] text-muted-foreground">
                  {label.replaceAll('_', ' ')}
                </span>
              ))
            ) : (
              // No classifier labels means the nightly reply-rate sweep raised
              // it, not a reply — worth saying, since the evidence reads
              // differently.
              <span className="text-[11px] text-muted-foreground">reply-rate detector</span>
            )}
          </span>
          <span className="text-[11px] leading-tight text-muted-foreground">
            {ACTION_COPY[item.action].effect}
          </span>
        </div>
      ),
    },
    {
      header: 'Until',
      width: '150px',
      render: (item) => {
        // On fund-scoped actions the date pauses the fund. On "Exclude this
        // person" it changes the verdict: with a date the person is paused
        // until then instead of excluded for good. Only MARK_INACTIVE pre-fills
        // a date, because only there does the classifier propose one.
        if (item.status !== 'PENDING') {
          return <CellText>{formatDate(item.proposedInactiveUntil)}</CellText>
        }
        return (
          <Input
            type="date"
            className="h-8 text-xs"
            value={dateEdits[item.id] ?? toDateInputValue(item.proposedInactiveUntil)}
            onChange={(e) => setDateEdits((prev) => ({ ...prev, [item.id]: e.target.value }))}
          />
        )
      },
    },
    {
      header: 'Raised',
      width: '110px',
      render: (item) => <CellText>{formatDate(item.createdAt)}</CellText>,
    },
    {
      header: '',
      width: '170px',
      render: (item) => {
        if (item.status !== 'PENDING') {
          return (
            <div className="flex flex-col leading-tight">
              <Badge variant={STATUS_VARIANT[item.status]} size="sm">
                {item.status}
              </Badge>
              <span className="text-[11px] text-muted-foreground">
                {formatDate(item.decidedAt)}
              </span>
            </div>
          )
        }

        const isDeciding = decide.isPending && decide.variables?.path?.id === item.id
        const editedDate = dateEdits[item.id]
        const inactiveUntil = editedDate ? fromDateInputValue(editedDate) : undefined

        return (
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={isDeciding}
              onClick={() =>
                decide.mutate({
                  path: { id: item.id },
                  body: { status: 'APPROVED', ...(inactiveUntil ? { inactiveUntil } : {}) },
                })
              }
            >
              {isDeciding ? '…' : 'Approve'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={isDeciding}
              onClick={() => decide.mutate({ path: { id: item.id }, body: { status: 'REJECTED' } })}
            >
              Reject
            </Button>
          </div>
        )
      },
    },
  ]

  return (
    <div className="w-full space-y-6">
      <div className="rounded-xl border border-border/60 bg-card/40 p-4">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Suggested outreach exclusions, raised by the reply classifier and the nightly reply-farm
          sweep. Nothing here is applied until you approve it — approving is what removes an
          investor or a contact from every founder&rsquo;s match pool.
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSearch({ status: s })}
              className={`rounded px-3 py-1 text-sm ${
                selectedStatus === s
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {([undefined, ...ACTIONS] as const).map((a) => (
            <button
              key={a ?? 'all'}
              type="button"
              onClick={() => setSearch({ action: a })}
              className={`rounded px-3 py-1 text-sm ${
                selectedAction === a
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {a ? ACTION_COPY[a].label : 'All actions'}
            </button>
          ))}
        </div>
      </div>

      {decide.isError ? (
        <p className="text-sm text-destructive">
          Could not save that decision. It may have already been decided by someone else — reload
          the page.
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        emptyMessage="Nothing to review."
        keyExtractor={(item) => item.id}
        renderExpanded={(item) =>
          openConversationId === item.id && item.sourceMessageId ? (
            <ConversationPanel messageId={item.sourceMessageId} />
          ) : null
        }
      />

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        onPageChange={(p) =>
          void navigate({
            search: { page: p, status: selectedStatus, action: selectedAction },
          })
        }
      />
    </div>
  )
}
